import React, { Dispatch, SetStateAction, useRef, memo, RefObject } from 'react';
import { Flex, Text } from '@radix-ui/themes';
import BidHistoryRow from './BidHistoryRow';
import { Button } from '@/app/components/ui/button';
import PersonalBidHistoryRow from './PersonalBidHistoryRow';
import { Skeleton } from '@/app/components/ui/skeleton';
import { AuctionState } from '@prisma/client';
import { NoncedBid } from './util';

interface Props {
  bids: NoncedBid[];
  showAll: boolean;
  setShowAll: Dispatch<SetStateAction<boolean>>;
  personal: boolean;
  loading: boolean;
  currentUserId: string | undefined;
  auctionState: AuctionState;
}

const DEFAULT_ROW_COUNT = 3;

const BidHistoryGroup = (
  { bids, showAll, setShowAll, personal, loading, currentUserId, auctionState }: Props
) => {
  const toggleShowAll = () => setShowAll(!showAll);

  // 36px comes the height of the BidHistoryRow, which
  // instead of being styled h-[36px] is styled py-1.5.
  // The former causes the rows to shrink and grow
  // during animation.
  const maxHeight = `${36 * bids.length}px`;
  const minHeight = `${36 * Math.min(DEFAULT_ROW_COUNT, bids.length)}px`

  if (loading) {
    return <Skeleton className="w-full h-10" />
  } else if (personal && bids.length === 0) {
    return (
      <Flex className="text-sm">
        <Text>You have no bids on record for this lot.</Text>
      </Flex>
      )
  } else if (bids.length === 0 || auctionState === AuctionState.PREVIEW) {
    return (
      <Flex className="text-sm">
        <Text>There are no bids on record for this lot.</Text>
      </Flex>
    )
  }

  return (
    <Flex direction="column" className="w-full text-xs sm:text-sm">
      <Flex
        direction="column"
        className={`overflow-hidden transition-[height] duration-300 ease-in-out`}
        style={{ height: showAll ? maxHeight : minHeight }}
      >
        {
          bids.slice(0, showAll ? bids.length : DEFAULT_ROW_COUNT).map((bid, index) => personal ?
            (
              <PersonalBidHistoryRow key={index} bid={bid} />
            ) : (
              <BidHistoryRow key={index} bid={bid} currentUserId={currentUserId} />
            ))
        }
      </Flex>
      {
        bids.length > DEFAULT_ROW_COUNT && (
          <Button variant="outline" onClick={toggleShowAll} className="mt-4">
            {showAll ? 'Show fewer' : `Show ${bids.length - DEFAULT_ROW_COUNT} more`}
          </Button>
        )
      }
    </Flex >
  );
};

export default BidHistoryGroup;
